import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import { createSubjectChangeRequest, fetchTeacherCurrentTermSubjects } from "../../utils/termSubjectApi";
import { getSchoolIdForUser } from "../../utils/userContext";

export default function TeacherMySubjects() {
    const { currentUser } = useSelector((s) => s.user);
    const teacherId = currentUser?._id || currentUser?.id;
    const schoolId = getSchoolIdForUser(currentUser);

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [message, setMessage] = useState("");
    const [dialog, setDialog] = useState(null);
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!teacherId) return;
        setLoading(true);
        try {
            const result = await fetchTeacherCurrentTermSubjects(teacherId);
            setData(result);
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not load your subjects.");
        } finally {
            setLoading(false);
        }
    }, [teacherId]);

    useEffect(() => {
        load();
    }, [load]);

    const periodLabel = data?.period
        ? [data.period.term, data.period.academicYear].filter(Boolean).join(" · ")
        : "Current term";

    const handleSubmitRequest = async () => {
        if (!dialog) return;
        setSaving(true);
        try {
            await createSubjectChangeRequest({
                schoolId,
                requestedByTeacherId: teacherId,
                subjectId: dialog.subjectId,
                classId: dialog.classId,
                requestType: "Unassign",
                reason,
            });
            setDialog(null);
            setReason("");
            setMessage("Change request submitted. Your form teacher will review it first.");
            await load();
        } catch (err) {
            setMessage(err.response?.data?.message || "Could not submit request.");
        } finally {
            setSaving(false);
        }
    };

    if (loading && !data) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const assignments = data?.assignments || [];

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
                <MenuBookOutlinedIcon color="primary" />
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        My subjects this term
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Assignments for {periodLabel} only. To change mid-term, submit a request for form teacher and
                        director approval.
                    </Typography>
                </Box>
            </Stack>

            {message ? (
                <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage("")}>
                    {message}
                </Alert>
            ) : null}

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Subject</TableCell>
                            <TableCell>Class</TableCell>
                            <TableCell>Periods/week</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {assignments.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4}>
                                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                                        No subjects assigned for the current term yet.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            assignments.map((a) => {
                                const sub = a.subject || {};
                                const cls = a.sclass || sub.sclassName || {};
                                return (
                                    <TableRow key={a._id || a.id}>
                                        <TableCell sx={{ fontWeight: 600 }}>{sub.subName || "—"}</TableCell>
                                        <TableCell>{cls.sclassName || "—"}</TableCell>
                                        <TableCell>{sub.sessions || "—"}</TableCell>
                                        <TableCell align="right">
                                            <Button
                                                size="small"
                                                onClick={() =>
                                                    setDialog({
                                                        subjectId: sub._id || sub.id,
                                                        classId: a.classId || cls._id || cls.id,
                                                        subjectName: sub.subName,
                                                    })
                                                }
                                            >
                                                Request change
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </Paper>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <Chip size="small" label={`${assignments.length} subject(s)`} variant="outlined" />
                <Chip size="small" label={periodLabel} color="primary" variant="outlined" />
            </Stack>

            <Dialog open={Boolean(dialog)} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Request subject change</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Request to be unassigned from <strong>{dialog?.subjectName}</strong>. Your form teacher reviews
                        first, then the school director approves.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={3}
                        label="Reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(null)}>Cancel</Button>
                    <Button variant="contained" disabled={saving || !reason.trim()} onClick={handleSubmitRequest}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
